// The jump bar's recents and pins: subjects by their key (subject.ts
// `subjectKey`) and repositories by `repo:owner/repo` (rows.ts `repoId`),
// each with the title and detail its row showed, kept per user in
// localStorage. Every write reads storage afresh and changes that, so two
// tabs never write over each other's; another tab's change is heard
// through the `storage` event. The repository index reads its pins here. They are a per-viewer convenience: storage that is
// missing or refuses a write loses them and nothing else, and they are
// deleted with the rest of the user's local state (state/prefs.svelte.ts
// `clearLocalUserState`).
import { readKey } from '../api/relaySubject';
import { isGitHubName } from '../subject';
import { repoId, type Repo } from './rows';
import type { Place } from './rows';

export type { Place };

/** What is kept for one user. */
export interface Kept {
  /** The latest visited first. */
  recents: readonly Place[];
  /** In the order pinned. */
  pins: readonly Place[];
}

export const MAX_RECENTS = 20;
/** Pinned subjects, at most; repositories have a cap of their own (`MAX_REPO_PINS`), so pinning on the repository index never refuses a pull request's pin. */
export const MAX_PINS = 50;
export const MAX_REPO_PINS = 50;
const isRepoKey = (key: string) => key.startsWith('repo:');

/** Every user's key starts with this. */
export const PLACES_PREFIX = 'changelist:jump:';
export const placesKey = (login: string) => PLACES_PREFIX + login.toLowerCase();

const EMPTY: Kept = { recents: [], pins: [] };

/** A kept key in its canonical spelling: a subject's, or a repository's `repo:owner/repo`; null when it is neither. */
function keyOf(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const repo = /^repo:([^/]+)\/([^/]+)$/.exec(v);
  if (repo) return isGitHubName(repo[1]) && isGitHubName(repo[2]) ? repoId({ owner: repo[1], repo: repo[2] }) : null;
  return readKey(v);
}

function placesOf(v: unknown, max: number): Place[] {
  if (!Array.isArray(v)) return [];
  const out: Place[] = [];
  for (const x of v) {
    const key = keyOf((x as Place | null)?.key);
    if (!key || out.some((p) => p.key === key)) continue;
    const { title, detail } = x as Place;
    out.push({ key, ...(typeof title === 'string' && title ? { title } : {}), ...(typeof detail === 'string' && detail ? { detail } : {}) });
  }
  return out.slice(0, max);
}

/** What was kept, from its stored text: places whose key reads back, in its canonical spelling. */
export function decodeKept(raw: string | null): Kept {
  if (!raw) return EMPTY;
  try {
    const v = JSON.parse(raw) as { recents?: unknown; pins?: unknown } | null;
    const pins = placesOf(v?.pins, MAX_PINS + MAX_REPO_PINS);
    // Each kind to its own cap, in the order pinned.
    let subjects = 0, repos = 0;
    return { recents: placesOf(v?.recents, MAX_RECENTS), pins: pins.filter((p) => (isRepoKey(p.key) ? ++repos <= MAX_REPO_PINS : ++subjects <= MAX_PINS)) };
  } catch {
    return EMPTY;
  }
}

/** `place` with what `had` said of it that it does not: a visit naming no title keeps the title seen before. */
const over = (place: Place, had: Place | undefined): Place => {
  const title = place.title ?? had?.title;
  const detail = place.detail ?? had?.detail;
  return { key: place.key, ...(title ? { title } : {}), ...(detail ? { detail } : {}) };
};

/** `place` first in `list`, once. */
function first(list: readonly Place[], place: Place, max: number): Place[] {
  return [over(place, list.find((p) => p.key === place.key)), ...list.filter((p) => p.key !== place.key)].slice(0, max);
}

export interface PlacesOptions {
  /** Where they are kept: localStorage, or null without it. */
  storage: () => Storage | null;
  /** The signed-in user, or null: nobody's are kept. */
  login: () => string | null;
  /** Hear another tab change them (the window's `storage` event); the function returned stops. */
  listen?: (changed: () => void) => () => void;
}

export class Places {
  readonly #storage: () => Storage | null;
  readonly #login: () => string | null;
  /** What each user's are, as last read or written on this page. */
  readonly #kept = new Map<string, Kept>();
  readonly #listeners = new Set<() => void>();
  #version = $state(0);

  constructor(o: PlacesOptions) {
    this.#storage = o.storage;
    this.#login = o.login;
    o.listen?.(() => this.refresh());
  }

  #user(): string | null {
    const login = this.#login();
    return login ? login.toLowerCase() : null;
  }

  #load(user: string): Kept {
    let raw: string | null = null;
    try {
      raw = this.#storage()?.getItem(placesKey(user)) ?? null;
    } catch {
      /* storage unavailable: what this page holds is all there is */
      return this.#kept.get(user) ?? EMPTY;
    }
    // Without storage, this page's copy is all there is.
    if (raw === null && !this.#storage()) return this.#kept.get(user) ?? EMPTY;
    const kept = decodeKept(raw);
    this.#kept.set(user, kept);
    return kept;
  }

  #current(): Kept {
    void this.#version;
    const user = this.#user();
    if (!user) return EMPTY;
    return this.#kept.get(user) ?? this.#load(user);
  }

  #changed() {
    this.#version++;
    for (const f of [...this.#listeners]) f();
  }

  /** Change what is kept, read afresh from storage (another tab may have written since): the change is made to that. */
  #write(change: (k: Kept) => Kept): void {
    const user = this.#user();
    if (!user) return;
    const next = change(this.#load(user));
    this.#kept.set(user, next);
    try {
      this.#storage()?.setItem(placesKey(user), JSON.stringify(next));
    } catch {
      /* storage unavailable or full: they last as long as the page */
    }
    this.#changed();
  }

  get recents(): readonly Place[] {
    return this.#current().recents;
  }

  get pins(): readonly Place[] {
    return this.#current().pins;
  }

  isPinned(key: string): boolean {
    return this.#current().pins.some((p) => p.key === key);
  }

  /** The subject was visited: first among the recents. */
  visit(place: Place): void {
    this.#write((k) => ({ ...k, recents: first(k.recents, place, MAX_RECENTS) }));
  }

  /**
   * Pin a subject or a repository; a pinned one keeps its place, with the
   * title given. False when MAX_PINS are pinned already: nothing is
   * pinned, and the person is to be told so.
   */
  pin(place: Place): boolean {
    let pinned = true;
    this.#write((k) => {
      const had = k.pins.find((p) => p.key === place.key);
      if (had) return { ...k, pins: k.pins.map((p) => (p === had ? over(place, had) : p)) };
      const repo = isRepoKey(place.key);
      if (k.pins.filter((p) => isRepoKey(p.key) === repo).length >= (repo ? MAX_REPO_PINS : MAX_PINS)) {
        pinned = false;
        return k;
      }
      return { ...k, pins: [...k.pins, place] };
    });
    return pinned && !!this.#user();
  }

  unpin(key: string): void {
    this.#write((k) => ({ ...k, pins: k.pins.filter((p) => p.key !== key) }));
  }

  /** The repositories pinned, in the order pinned, as GitHub spells them when that was given (for the repository index). */
  pinnedRepos(): Repo[] {
    return this.pins.flatMap((p) => {
      const m = /^repo:([^/]+)\/(.+)$/.exec(p.key);
      if (!m) return [];
      const spelled = p.title && /^([^/]+)\/([^/]+)$/.exec(p.title);
      return [spelled && repoId({ owner: spelled[1], repo: spelled[2] }) === p.key ? { owner: spelled[1], repo: spelled[2] } : { owner: m[1], repo: m[2] }];
    });
  }

  /** Pin a repository, keeping `r`'s spelling (GitHub's, as its caller has it) as the pin's title; false past MAX_REPO_PINS. */
  pinRepo(r: Repo): boolean {
    return this.pin({ key: repoId(r), title: `${r.owner}/${r.repo}` });
  }

  unpinRepo(r: Repo): void {
    this.unpin(repoId(r));
  }

  /** Be told whenever they change, here or in another tab; the function returned stops. */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => void this.#listeners.delete(listener);
  }

  /** Read them again from storage: another tab may have changed them. */
  refresh(): void {
    this.#kept.clear();
    this.#changed();
  }
}
